import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useTeamChat } from '@/context/TeamChatContext';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Bold, Italic, Link, ListOrdered, List, Code, AtSign, Paperclip, Send, Type, X, FileText, Loader as Loader2, Mic, Square, Play, Pause } from 'lucide-react';
import { cn } from '@/lib/utils';
import EmojiPicker from './EmojiPicker';
import GifPicker from './GifPicker';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { uploadToR2 } from '@/lib/r2-storage';
import VoiceMessagePlayer from './VoiceMessagePlayer';
import LinkPreview from './LinkPreview';

// Helper to extract unique URLs for previews
const extractUrls = (text: string): string[] => {
  const urls: string[] = [];
  const regex = /https?:\/\/[^\s<]+[^<.,:;"')\]\s]/g;
  let match;
  while ((match = regex.exec(text)) !== null) {
    urls.push(match[0]);
  }
  return [...new Set(urls)];
};

interface Attachment {
  file: File;
  id: string;
  uploading: boolean;
  progress: number;
  url?: string;
  error?: string;
  previewUrl?: string;
  duration?: number;
}

interface MessageComposerProps {
  channelId?: string;
  dmId?: string;
  threadId?: string;
  placeholder?: string;
  isGlobalDarkMode: boolean;
  isMobile?: boolean;
  hasRightPanel?: boolean;
}

const ALLOWED_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'audio/webm',
  'audio/mp4',
  'audio/mpeg',
  'audio/ogg',
];

const MessageComposer: React.FC<MessageComposerProps> = ({
  channelId,
  dmId,
  threadId,
  placeholder = "Message",
  isGlobalDarkMode,
  isMobile = false,
  hasRightPanel = false
}) => {
  const { sendMessage, channels, directMessages, getDmParticipant, users, currentUser, setTyping, isChannelMember, joinChannel } = useTeamChat();
  const [message, setMessage] = useState('');
  const [showFormatting, setShowFormatting] = useState(true);
  const [alsoSendToChannel, setAlsoSendToChannel] = useState(false);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // --- Voice recording state ---
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingSecondsRef = useRef<number>(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const MAX_RECORDING_SECONDS = 120; // 2 minutes

  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  
  // Link preview state
  const [dismissedPreviews, setDismissedPreviews] = useState<Set<string>>(new Set());
  const composerUrls = extractUrls(message).filter(url => !dismissedPreviews.has(url));

  // ---- Voice recording helpers ----
  const cancelRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    audioChunksRef.current = [];
    setIsRecording(false);
    setIsPaused(false);
    setRecordingSeconds(0);
    recordingSecondsRef.current = 0;
  }, []);

  useEffect(() => {
    setMessage('');
    setAttachments([]);
    setAlsoSendToChannel(false);
    setDismissedPreviews(new Set());
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    cancelRecording();
  }, [channelId, dmId, threadId, cancelRecording]);

  const uploadFile = async (file: File, durationSec?: number) => {
    const id = Math.random().toString(36).substring(7);
    const isAudio = file.type.startsWith('audio/');
    const previewUrl = file.type.startsWith('image/') || isAudio ? URL.createObjectURL(file) : undefined;

    const newAttachment: Attachment = {
      file,
      id,
      uploading: true,
      progress: 0,
      previewUrl,
      duration: durationSec
    };

    setAttachments(prev => [...prev, newAttachment]);

    if (!currentUser) return;

    try {
      const progressInterval = setInterval(() => {
        setAttachments(prev => prev.map(a =>
          a.id === id && a.uploading ? { ...a, progress: Math.min(a.progress + 10, 90) } : a
        ));
      }, 300);

      const { publicUrl } = await uploadToR2({
        fileName: file.name,
        file,
        folder: 'chat_attachments'
      });

      clearInterval(progressInterval);

      setAttachments(prev => prev.map(a =>
        a.id === id ? { ...a, uploading: false, progress: 100, url: publicUrl } : a
      ));

    } catch (error) {
      console.error("Upload error", error);
      setAttachments(prev => prev.map(a =>
        a.id === id ? { ...a, uploading: false, error: 'Failed' } : a
      ));
    }
  };

  const startRecording = useCallback(async () => {
    if (!currentUser) return;
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      alert('Microphone access was denied. Please allow microphone permissions and try again.');
      return;
    }

    const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
      ? 'audio/webm;codecs=opus'
      : MediaRecorder.isTypeSupported('audio/mp4')
        ? 'audio/mp4'
        : '';

    const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    audioChunksRef.current = [];

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };

    recorder.onstop = () => {
      stream.getTracks().forEach(t => t.stop());
      const chunks = audioChunksRef.current;
      if (chunks.length === 0) return;

      const durationSec = recordingSecondsRef.current;
      const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });
      const ext = (mimeType || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: mimeType || 'audio/webm' });
      
      // Stop and Preview: just upload to attachments array
      uploadFile(file, durationSec);
    };

    mediaRecorderRef.current = recorder;
    recorder.start(250); 
    setIsRecording(true);
    setIsPaused(false);
    setRecordingSeconds(0);
    recordingSecondsRef.current = 0;

    recordingTimerRef.current = setInterval(() => {
      setRecordingSeconds(prev => {
        const next = prev + 1;
        recordingSecondsRef.current = next;
        if (next >= MAX_RECORDING_SECONDS) {
          if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
          if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
            mediaRecorderRef.current.stop();
          }
          setIsRecording(false);
          setIsPaused(false);
          return prev;
        }
        return next;
      });
    }, 1000);
  }, [currentUser]);

  const stopRecording = useCallback(() => {
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    setIsPaused(false);
  }, []);

  const pauseRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.pause();
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      setIsPaused(true);
    }
  }, []);

  const resumeRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'paused') {
      mediaRecorderRef.current.resume();
      recordingTimerRef.current = setInterval(() => {
        setRecordingSeconds(prev => {
          const next = prev + 1;
          recordingSecondsRef.current = next;
          if (next >= MAX_RECORDING_SECONDS) {
            stopRecording();
            return prev;
          }
          return next;
        });
      }, 1000);
      setIsPaused(false);
    }
  }, [stopRecording]);



  const processFiles = (files: FileList | File[]) => {
    const validFiles = Array.from(files).filter(file => ALLOWED_TYPES.includes(file.type));
    validFiles.forEach(file => uploadFile(file));
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      e.target.value = '';
    }
  };

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Check if we are actually leaving the container (and not just entering a child)
    if (e.currentTarget.contains(e.relatedTarget as Node)) {
      return;
    }

    setIsDragging(false);
  }, []);

  const removeAttachment = (id: string) => {
    setAttachments(prev => prev.filter(a => a.id !== id));
  };

  const activeChannel = channels.find(c => c.id === channelId);
  const activeDm = directMessages.find(d => d.id === dmId);
  const dmParticipant = activeDm ? getDmParticipant(activeDm) : undefined;

  const getPlaceholderText = () => {
    if (threadId) return "Reply...";
    if (channelId) {
      const channel = channels.find(c => c.id === channelId);
      return `Message #${channel?.name || 'group'}`;
    }
    if (dmId) {
      const dm = directMessages.find(d => d.id === dmId);
      if (dm) {
        const participant = getDmParticipant(dm);
        return `Message ${participant?.displayName || 'user'}`;
      }
    }
    return placeholder;
  };

  const insertFormat = (format: string) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = message;

    let before = text.substring(0, start);
    let selection = text.substring(start, end);
    let after = text.substring(end);

    let newText = "";

    switch (format) {
      case 'bold':
        newText = `${before}**${selection || 'bold'}**${after}`;
        break;
      case 'italic':
        newText = `${before}_${selection || 'italic'}_${after}`;
        break;
      case 'link':
        newText = `${before}[${selection || 'text'}](url)${after}`;
        break;
      case 'list-ol':
        newText = `${before}\n1. ${selection || 'item'}${after}`;
        break;
      case 'list-ul':
        newText = `${before}\n- ${selection || 'item'}${after}`;
        break;
      case 'code':
        newText = `${before}\`${selection || 'code'}\`${after}`;
        break;
      default:
        return;
    }

    setMessage(newText);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleEmojiSelect = (emoji: string) => {
    if (!textareaRef.current) return;
    const start = textareaRef.current.selectionStart;
    const end = textareaRef.current.selectionEnd;
    const text = message;

    const newText = text.substring(0, start) + emoji + text.substring(end);
    setMessage(newText);
    setTimeout(() => {
      textareaRef.current?.focus();
    }, 0);
  };

  const handleGifSelect = (gif: { id: string; title: string; url: string; originalUrl: string }) => {
    const gifAttachment = [{
      name: gif.title || 'GIF',
      size: 0,
      type: 'image/gif',
      url: gif.url,
    }];
    sendMessage('', channelId, dmId, threadId, alsoSendToChannel, gifAttachment as any);
  };

  const handleSubmit = () => {
    const isUploading = attachments.some(a => a.uploading);
    if (isUploading) return;

    const validAttachments = attachments
      .filter(a => !a.error && a.url)
      .map(a => ({
        name: a.file.name,
        size: a.file.size,
        type: a.file.type,
        url: a.url!
      }));

    if (message.trim() || validAttachments.length > 0) {
      sendMessage(message.trim(), channelId, dmId, threadId, alsoSendToChannel, validAttachments as any, Array.from(dismissedPreviews));
      setMessage('');
      setAttachments([]);
      setAlsoSendToChannel(false);
      setDismissedPreviews(new Set());
      setShowMentions(false);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (channelId || dmId) setTyping(channelId || dmId!, false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      if (showMentions) return;
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = e.target.value;
    setMessage(val);
    
    // Broadcast Typed Event
    const targetId = channelId || dmId;
    if (targetId) {
      setTyping(targetId, true);
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        setTyping(targetId, false);
      }, 2500);
    }

    const cursor = e.target.selectionStart;
    const textBefore = val.substring(0, cursor);
    const lastAt = textBefore.lastIndexOf('@');

    if (lastAt !== -1) {
      const query = textBefore.substring(lastAt + 1);
      if (!query.includes(' ') && !query.includes('\n')) {
        setMentionQuery(query);
        setShowMentions(true);
        return;
      }
    }
    setShowMentions(false);
  };

  const insertMention = (displayName: string) => {
    if (!textareaRef.current) return;
    const cursor = textareaRef.current.selectionStart;
    const text = message;
    const textBefore = text.substring(0, cursor);
    const lastAt = textBefore.lastIndexOf('@');
    const textAfter = text.substring(cursor);

    const newText = text.substring(0, lastAt) + `@${displayName} ` + textAfter;
    setMessage(newText);
    setShowMentions(false);
    setTimeout(() => textareaRef.current?.focus(), 0);
  };

  const chatMention = { id: '__chat__', displayName: 'chat', name: 'chat', isGroupMention: true };
  const matchesChat = 'chat'.includes(mentionQuery.toLowerCase());
  let availableUsersForMention = users;
  if (activeChannel) {
    availableUsersForMention = users.filter(u => activeChannel.members.includes(u.id));
  } else if (activeDm) {
    availableUsersForMention = users.filter(u => activeDm.participants.includes(u.id));
  }

  const filteredUsers = availableUsersForMention.filter(u =>
    (u.displayName.toLowerCase().includes(mentionQuery.toLowerCase()) ||
      u.name.toLowerCase().includes(mentionQuery.toLowerCase())) &&
    u.id !== currentUser?.id
  ).slice(0, 5);
  const mentionSuggestions = [
    ...(matchesChat && channelId ? [chatMention] : []),
    ...filteredUsers.map(u => ({ ...u, isGroupMention: false })),
  ].slice(0, 6);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  }, [message]);

  const composerBg = isGlobalDarkMode ? 'bg-card' : 'bg-white';
  const borderColor = isGlobalDarkMode ? 'border-border' : 'border-gray-200';
  const toolbarBg = isGlobalDarkMode ? 'bg-background/40' : 'bg-gray-50';
  const popoverBg = isGlobalDarkMode ? 'bg-card border-border' : 'bg-white border-gray-200';
  const textColor = isGlobalDarkMode ? 'text-foreground' : 'text-gray-900';
  const subTextColor = isGlobalDarkMode ? 'text-muted-foreground' : 'text-gray-500';

  const isUploading = attachments.some(a => a.uploading);
  const isSendingDisabled = (!message.trim() && attachments.length === 0) || isUploading;

  // Check if user is a member (only for channels, not DMs)
  const isMember = channelId ? isChannelMember(channelId) : true;
  const isPublicChannel = activeChannel && !activeChannel.isPrivate;

  // If not a member of a public channel, show join prompt
  if (channelId && !isMember && isPublicChannel) {
    return (
      <div className={cn("px-6 md:px-10 pb-4")}>
        <div className={cn(
          "border rounded-xl p-4 flex items-center justify-between",
          borderColor,
          composerBg
        )}>
          <div className="flex-1">
            <p className={cn("text-sm font-medium", textColor)}>
              You're viewing this channel
            </p>
            <p className={cn("text-xs", subTextColor)}>
              Join to send messages and participate in the conversation
            </p>
          </div>
          <Button
            onClick={() => joinChannel(channelId)}
            className="bg-workspace-accent hover:bg-workspace-accent-hover text-white"
          >
            Join Channel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn("relative", isMobile ? "pb-0" : (
        threadId 
          ? `pl-4 ${hasRightPanel ? 'pr-4' : 'pr-6 md:pr-10'} pb-4` 
          : `pl-6 md:pl-10 ${hasRightPanel ? 'pr-4' : 'pr-6 md:pr-10'} pb-4`
      ))}
    >
      {/* ---- Recording UI overlay ---- */}
      {isRecording && (
        <div className={cn(
          "absolute bottom-full mb-2 z-50 rounded-xl border shadow-xl flex items-center gap-3 px-4 py-3",
          isMobile ? "left-0 right-0 mx-0 rounded-none border-x-0" : "left-4 right-4",
          borderColor, composerBg
        )}>
          {/* Pulsing dot */}
          <span className="relative flex h-3 w-3 shrink-0">
            <span className={cn("absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75", !isPaused && "animate-ping")} />
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500" />
          </span>
          <span className={cn('text-sm font-semibold tabular-nums', textColor)}>
            {Math.floor(recordingSeconds / 60)}:{(recordingSeconds % 60).toString().padStart(2, '0')}
          </span>
          <span className={cn('text-xs', subTextColor)}>
            {isPaused ? 'Paused' : 'Recording…'} (max 2 min)
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              onClick={cancelRecording}
              className={cn(
                'text-xs px-3 py-1.5 rounded-md font-medium transition-colors',
                isGlobalDarkMode ? 'bg-muted text-muted-foreground hover:bg-accent' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={isPaused ? resumeRecording : pauseRecording}
              className={cn(
                'text-xs px-3 py-1.5 rounded-md font-medium transition-colors flex items-center gap-1.5',
                isGlobalDarkMode ? 'bg-muted text-muted-foreground hover:bg-accent' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              )}
            >
              {isPaused ? <Play className="w-3 h-3 fill-current" /> : <Pause className="w-3 h-3 fill-current" />}
              {isPaused ? 'Resume' : 'Pause'}
            </button>
            <button
              type="button"
              onClick={stopRecording}
              className="text-xs px-3 py-1.5 rounded-md font-medium bg-workspace-accent text-white hover:bg-workspace-accent/90 transition-colors flex items-center gap-1.5"
            >
              <Square className="w-3 h-3 fill-current" />
              Stop & Preview
            </button>
          </div>
        </div>
      )}
      {/* Mentions Popover */}
      {showMentions && mentionSuggestions.length > 0 && (
        <div className={cn("absolute bottom-full mb-2 w-64 border rounded-md shadow-lg z-50 overflow-hidden", isMobile ? "left-2" : "left-4", popoverBg)}>
          <div className="p-1">
            {mentionSuggestions.map(item => (
              <button
                key={item.id}
                className={cn(
                  "w-full flex items-center gap-2 px-2 py-1.5 rounded text-sm text-left transition-colors",
                  isGlobalDarkMode ? "hover:bg-accent" : "hover:bg-accent"
                )}
                onClick={() => insertMention(item.displayName)}
              >
                {item.isGroupMention ? (
                  <>
                    <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", "bg-workspace-accent/20")}>
                      <AtSign className={cn("w-3.5 h-3.5", isGlobalDarkMode ? "text-workspace-accent" : "text-primary")} />
                    </div>
                    <div className="flex flex-col">
                      <span className={cn("font-medium leading-none", textColor)}>@chat</span>
                      <span className={cn("text-[10px]", subTextColor)}>Notify all members</span>
                    </div>
                  </>
                ) : (
                  <>
                    <Avatar className="w-6 h-6">
                      <AvatarFallback className={isGlobalDarkMode ? "text-[10px] bg-muted text-muted-foreground" : "text-[10px]"}>
                        {item.displayName.charAt(0)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex flex-col">
                      <span className={cn("font-medium leading-none", textColor)}>{item.displayName}</span>
                      <span className={cn("text-[10px]", subTextColor)}>@{item.name}</span>
                    </div>
                  </>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      <div
        className={cn(
          "border transition-all duration-200 relative",
          isMobile ? "rounded-none border-x-0 border-b-0" : "rounded-xl",
          borderColor,
          composerBg,
          isGlobalDarkMode && "shadow-xl shadow-black/20",
          isDragging && "ring-2 ring-workspace-accent"
        )}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {/* Drag Overlay */}
        {isDragging && (
          <div className={cn("absolute inset-0 bg-workspace-accent/10 z-50 flex items-center justify-center backdrop-blur-[1px]", isMobile ? "rounded-none" : "rounded-xl")}>
            <div className="bg-background px-4 py-2 rounded-full shadow-lg flex items-center gap-2">
              <Paperclip className="w-4 h-4 text-workspace-accent" />
              <span className="text-sm font-semibold text-workspace-accent">Drop files here</span>
            </div>
          </div>
        )}

        {/* Formatting Toolbar - Top */}
        {showFormatting && (
          <div className={cn("flex items-center px-2 py-1.5 border-b overflow-x-auto", borderColor, toolbarBg)}>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('bold')}
                title="Bold"
              >
                <Bold className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('italic')}
                title="Italic"
              >
                <Italic className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('link')}
                title="Link"
              >
                <Link className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('list-ol')}
                title="Ordered List"
              >
                <ListOrdered className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('list-ul')}
                title="Bullet List"
              >
                <List className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className={cn("h-7 w-7", isGlobalDarkMode ? "hover:bg-accent text-muted-foreground" : "hover:bg-gray-100 text-gray-600")}
                onClick={() => insertFormat('code')}
                title="Code"
              >
                <Code className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        {/* Attachments Preview */}
        {attachments.length > 0 && (
          <div className="flex flex-col gap-2 px-3 pt-3">
            {/* Audio Attachments */}
            {attachments.filter(a => a.file.type.startsWith('audio/')).map((a) => (
              <div key={a.id} className="relative w-full sm:w-max group">
                <button
                  type="button"
                  onClick={() => removeAttachment(a.id)}
                  className="absolute -top-2 -right-2 z-10 w-5 h-5 bg-red-500 rounded-full text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  title="Remove voice message"
                >
                  <X className="w-3 h-3" />
                </button>
                <VoiceMessagePlayer
                  url={a.previewUrl || ''}
                  duration={a.duration}
                  isGlobalDarkMode={isGlobalDarkMode}
                  isOwn={true}
                />
              </div>
            ))}

            {/* Other Attachments */}
            {attachments.filter(a => !a.file.type.startsWith('audio/')).length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.filter(a => !a.file.type.startsWith('audio/')).map((a) => (
                  <div key={a.id} className={cn("flex items-center gap-3 p-2 rounded-md border max-w-[280px]", isGlobalDarkMode ? "bg-card border-border" : "bg-gray-50 border-gray-200")}>
                    <div className={cn("w-10 h-10 rounded-md overflow-hidden flex items-center justify-center shrink-0 border relative", isGlobalDarkMode ? "bg-muted border-border" : "bg-gray-100 border-gray-200")}>
                      {a.file.type.startsWith('image/') && a.previewUrl ? (
                        <img src={a.previewUrl} alt={a.file.name} className="w-full h-full object-cover" />
                      ) : (
                        <FileText className={cn("w-5 h-5 opacity-50", isGlobalDarkMode ? "text-muted-foreground" : "text-gray-500")} />
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 mb-0.5">
                        <span className={cn("text-xs font-medium truncate", textColor)} title={a.file.name}>{a.file.name}</span>
                        <button
                          type="button"
                          onClick={() => removeAttachment(a.id)}
                          className="hover:text-red-500 transition-colors shrink-0"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>

                      {a.uploading ? (
                        <div className="flex items-center gap-2">
                          <div className="h-1 flex-1 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                            <div
                              className="h-full bg-workspace-accent transition-all duration-300"
                              style={{ width: `${a.progress}%` }}
                            />
                          </div>
                          <span className="text-[10px] opacity-70 w-6 text-right">{a.progress}%</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          {a.error ? (
                            <span className="text-[10px] text-destructive whitespace-nowrap">Failed</span>
                          ) : (
                            <span className="text-[10px] text-green-500 whitespace-nowrap">Ready</span>
                          )}
                          <span className={cn("text-[10px] truncate", subTextColor)}>
                            ({Math.round(a.file.size / 1024)}KB)
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* URL Previews while typing */}
        {composerUrls.length > 0 && (
          <div className="flex flex-col gap-2 px-3 pt-3">
             {composerUrls.map(url => (
                <div key={`preview-${url}`} className="relative group/preview w-fit">
                   <button
                     type="button"
                     onClick={() => setDismissedPreviews(prev => new Set(prev).add(url))}
                     className="absolute -top-2 -right-2 z-10 w-5 h-5 bg-gray-500 hover:bg-gray-600 rounded-full text-white flex items-center justify-center transition-colors shadow-sm"
                     title="Remove preview"
                   >
                     <X className="w-3 h-3" />
                   </button>
                   <div className="pointer-events-none sm:pointer-events-auto">
                     <LinkPreview url={url} isGlobalDarkMode={isGlobalDarkMode} />
                   </div>
                </div>
             ))}
          </div>
        )}

        {/* Textarea */}
        <Textarea
          ref={textareaRef}
          value={message}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={getPlaceholderText()}
          className={cn(
            "border-0 resize-none min-h-[44px] max-h-[200px] focus-visible:ring-0 focus-visible:ring-offset-0 px-3 py-2 text-sm",
            isGlobalDarkMode ? "text-foreground placeholder:text-muted-foreground" : "text-gray-900 placeholder:text-gray-400",
            composerBg,
            "bg-transparent"
          )}
          rows={1}
        />

        {/* Also send to channel checkbox - only show in thread */}
        {threadId && (activeChannel || activeDm) && (
          <div className={cn("flex items-center gap-2 px-3 py-2 border-t", borderColor)}>
            <Checkbox
              id="also-send-to-channel"
              checked={alsoSendToChannel}
              onCheckedChange={(checked) => setAlsoSendToChannel(checked === true)}
              className={isGlobalDarkMode ? "border-border data-[state=checked]:bg-workspace-accent data-[state=checked]:border-workspace-accent" : ""}
            />
            <label
              htmlFor="also-send-to-channel"
              className={cn("text-[11px] cursor-pointer select-none flex-1 min-w-0 truncate", subTextColor)}
            >
              {activeChannel
                ? `Also send to #${activeChannel.name}`
                : (dmParticipant ? `Also send to @${dmParticipant.displayName}` : "Also send to chat")
              }
            </label>
          </div>
        )}

        {/* Bottom Toolbar */}
        <div className={cn("flex items-center px-2 py-1.5 border-t gap-2", borderColor)}>
          <Button
            className={cn(
              "h-9 px-4 rounded-md transition-all duration-200 flex items-center gap-2 shrink-0",
              "bg-workspace-accent hover:bg-workspace-accent-hover text-white shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            )}
            onClick={handleSubmit}
            disabled={isSendingDisabled}
          >
            {isUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
            <span className="text-xs font-bold">{isUploading ? 'Uploading...' : 'Send'}</span>
          </Button>

          <div className="flex items-center gap-0.5 overflow-x-auto mobile-hide-scrollbar flex-1 min-w-0">
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-7 w-7", showFormatting ? (isGlobalDarkMode ? "bg-accent text-foreground" : "bg-muted text-gray-900") : (isGlobalDarkMode ? "text-muted-foreground hover:bg-accent" : "text-gray-500 hover:bg-gray-100"))}
              onClick={() => setShowFormatting(!showFormatting)}
              title={showFormatting ? "Hide formatting" : "Show formatting"}
            >
              <Type className="h-4 w-4" />
            </Button>
            <div className={cn("w-px h-4 mx-1 bg-border")} />
            <Button variant="ghost" size="icon" className={cn("h-7 w-7", isGlobalDarkMode ? "text-muted-foreground hover:bg-accent" : "text-gray-500 hover:bg-gray-100")} onClick={() => {
              const text = message + '@';
              setMessage(text);
              handleChange({ target: { value: text, selectionStart: text.length } } as any);
              textareaRef.current?.focus();
            }}>
              <AtSign className="h-4 w-4" />
            </Button>
            <EmojiPicker onSelect={handleEmojiSelect} side="top" isGlobalDarkMode={isGlobalDarkMode} />
            <GifPicker onSelect={handleGifSelect} isGlobalDarkMode={isGlobalDarkMode} />
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              multiple
              accept={ALLOWED_TYPES.join(',')}
              onChange={handleFileSelect}
            />
            <Button
              variant="ghost"
              size="icon"
              className={cn("h-7 w-7", isGlobalDarkMode ? "text-muted-foreground hover:bg-accent" : "text-gray-500 hover:bg-gray-100")}
              onClick={() => fileInputRef.current?.click()}
              title="Attach files"
            >
              <Paperclip className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className={cn(
                "h-7 w-7 transition-colors",
                isRecording
                  ? "text-red-500 bg-red-500/10 hover:bg-red-500/20"
                  : (isGlobalDarkMode ? "text-muted-foreground hover:bg-accent" : "text-gray-500 hover:bg-gray-100")
              )}
              onClick={isRecording ? stopRecording : startRecording}
              title={isRecording ? 'Stop recording' : 'Record voice message'}
            >
              <Mic className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MessageComposer;

